if (!customElements.get('rs7gt-landing')) {
  customElements.define('rs7gt-landing', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.video = this.querySelector('[data-background-video]');
      this.inView = true;
      this.sceneVisible = true;
      this.mediaAllowed = !this.querySelector('.rs7gt__app') || this.querySelector('rs7gt-signup')?.hasAttribute('data-form-ready');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.media = JSON.parse(this.querySelector('[data-media-config]').textContent);
      this.mobileQuery.addEventListener('change', () => this.updateMedia(), options);
      document.addEventListener('visibilitychange', () => {
        this.updatePlayback();
        this.updateCountdown();
      }, options);
      this.video.addEventListener('error', () => {
        if (this.fallbackSource && this.videoSource !== this.fallbackSource) {
          this.videoSource = this.fallbackSource;
          this.playPromise = null;
          this.updatePlayback();
          return;
        }
        this.video.hidden = true;
      }, options);
      this.addEventListener('rs7gt:form-ready', () => this.allowMedia(), options);
      this.addEventListener('rs7gt:visibilitychange', event => {
        this.sceneVisible = event.detail.visible;
        this.updatePlayback();
      }, options);
      this.visibilityObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.updatePlayback();
      });
      this.visibilityObserver.observe(this);
      ['pointerdown', 'keydown'].forEach(name => {
        this.addEventListener(name, () => this.allowMedia(), { ...options, passive: true });
      });
      this.setupCountdown();
      this.updateMedia();
      if (!this.mediaAllowed) this.mediaTimer = window.setTimeout(() => this.allowMedia(), 4000);
    }

    disconnectedCallback() {
      this.controller.abort();
      window.clearTimeout(this.countdownTimer);
      window.clearTimeout(this.mediaTimer);
      this.visibilityObserver.disconnect();
      this.stopMedia();
    }

    allowMedia() {
      window.clearTimeout(this.mediaTimer);
      this.mediaAllowed = true;
      this.updatePlayback();
    }

    setupCountdown() {
      this.countdown = this.querySelector('[data-countdown]');
      this.countdownEnd = NaN;
      if (!this.countdown) return;
      const value = this.countdown.dataset.end.trim();
      const parts = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(value);
      if (parts) {
        const calendarTime = Date.parse(`${parts[1]}Z`);
        if (Number.isFinite(calendarTime) && new Date(calendarTime).toISOString() === `${parts[1]}.000Z`) {
          this.countdownEnd = Date.parse(value);
        }
      }
      const valid = Number.isFinite(this.countdownEnd);
      const editing = this.dataset.designMode === 'true';
      this.countdown.hidden = !valid && !editing;
      this.toggleAttribute('data-has-countdown', valid || editing);
      this.countdown.querySelector('[data-countdown-display]').hidden = !valid;
      const note = this.countdown.querySelector('[data-countdown-editor-note]');
      if (note) note.hidden = valid;
      if (!valid) return;
      this.countdownFields = ['days', 'hours', 'minutes', 'seconds'].map(unit =>
        this.countdown.querySelector(`[data-countdown-${unit}]`)
      );
      this.countdownFormatters = ['day', 'hour', 'minute', 'second'].map(unit =>
        new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'unit', unit, unitDisplay: 'long' })
      );
      this.updateCountdown();
    }

    updateCountdown() {
      window.clearTimeout(this.countdownTimer);
      if (!Number.isFinite(this.countdownEnd)) return;
      const remaining = Math.max(0, Math.ceil((this.countdownEnd - Date.now()) / 1000));
      const values = [
        Math.floor(remaining / 86400),
        Math.floor(remaining / 3600) % 24,
        Math.floor(remaining / 60) % 60,
        remaining % 60
      ];
      const dayDigits = String(values[0]).padStart(2, '0').length;
      if (this.countdownFields[0].childElementCount !== dayDigits) {
        this.countdownFields[0].replaceChildren(...Array.from({ length: dayDigits }, () => document.createElement('span')));
      }
      if (this.countdownDayDigits !== dayDigits) {
        this.countdownDayDigits = dayDigits;
        this.countdown.style.setProperty('--rs7gt-days-digits', dayDigits);
        this.countdown.style.setProperty('--rs7gt-clock-units', (4.38 + 0.59 * dayDigits).toFixed(2));
      }
      values.forEach((value, index) => {
        const digits = String(value).padStart(2, '0');
        Array.from(this.countdownFields[index].children).forEach((digit, position) => {
          if (digit.textContent !== digits[position]) digit.textContent = digits[position];
        });
      });
      const description = values.map((value, index) => this.countdownFormatters[index].format(value));
      this.countdown.querySelector('[data-countdown-accessible]').textContent = description.join(', ');
      if (remaining && !document.hidden) {
        this.countdownTimer = window.setTimeout(() => this.updateCountdown(), 1000 - Date.now() % 1000);
      }
    }

    stopMedia() {
      this.playPromise = null;
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.video.hidden = true;
      this.animatedImage?.remove();
      this.animatedImage = null;
    }

    updateMedia() {
      this.stopMedia();
      const media = this.mobileQuery.matches ? this.media.mobile : this.media.desktop;
      const sources = (media.sources || []).filter(source => source.format === 'mp4');
      sources.sort((a, b) => a.width - b.width);
      const targetWidth = Math.min(window.innerWidth * Math.min(window.devicePixelRatio || 1, 1.5), 1920);
      const mp4 = sources.find(item => item.width >= targetWidth) || sources[sources.length - 1];
      const adaptive = this.video.canPlayType('application/vnd.apple.mpegurl')
        ? (media.sources || []).find(item => item.format === 'm3u8') : null;
      const source = adaptive || mp4;
      this.videoSource = source?.url;
      this.fallbackSource = mp4?.url;
      this.gifSource = !source && media.animated ? media.image : null;
      this.updatePlayback();
    }

    updatePlayback() {
      const shouldPlay = this.isConnected && this.mediaAllowed && this.inView && this.sceneVisible && !document.hidden;
      if (this.videoSource) {
        if (!shouldPlay) {
          this.playPromise = null;
          this.video.pause();
          return;
        }
        if (this.video.getAttribute('src') !== this.videoSource) {
          this.video.src = this.videoSource;
          this.playPromise = null;
        }
        this.video.hidden = false;
        this.video.muted = true;
        if (!this.video.paused || this.playPromise) return;
        const playback = this.video.play().catch(() => {
          if (this.playPromise === playback && this.video.paused) this.video.hidden = true;
        }).finally(() => {
          if (this.playPromise === playback) this.playPromise = null;
        });
        this.playPromise = playback;
      } else if (this.gifSource) {
        if (shouldPlay && !this.animatedImage) {
          this.animatedImage = new Image();
          this.animatedImage.className = 'rs7gt__video';
          this.animatedImage.alt = '';
          this.animatedImage.width = 1920;
          this.animatedImage.height = 1080;
          this.animatedImage.src = this.gifSource;
          this.querySelector('.rs7gt__media').append(this.animatedImage);
        } else if (!shouldPlay) {
          this.animatedImage?.remove();
          this.animatedImage = null;
        }
      }
    }

  });
}
